import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileDown, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, openPdf } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { act, useFetch } from "@/lib/hooks";
import { da, fmtDate, PAY_METHODS } from "@/lib/format";
import { Empty, Loading, PageHeader, Panel, PdfButton, StatusBadge, Table, cellCls, rowCls } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const FILTERS: [string, string, string][] = [["", "", "Toutes"], ["brouillon", "", "Brouillons"], ["validee", "impayee", "Impayées"], ["validee", "partielle", "Partielles"], ["validee", "payee", "Payées"]];

function CashReport() {
  const [day, setDay] = useState(new Date().toLocaleDateString("en-CA"));
  const open = () => openPdf("cash", day, `/cash/pdf?date=${day}`).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex items-center gap-2" data-testid="cash-report">
      <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-11 w-44" data-testid="cash-report-date" />
      <Button variant="outline" className="h-11" disabled={!day} onClick={open} data-testid="cash-report-btn"><FileDown className="mr-2 h-4 w-4" />Journal de caisse</Button>
    </div>
  );
}

function StampReport() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  if (!can(user?.role, "billing_validate")) return null;
  const open = () => openPdf("stamps", month, `/stamps/pdf?month=${month}`).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="stamp-report">
      <CashReport />
      <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-11 w-44" data-testid="stamp-report-month" />
      <Button variant="outline" className="h-11" disabled={!month} onClick={open} data-testid="stamp-report-btn"><FileDown className="mr-2 h-4 w-4" />Relevé des timbres</Button>
    </div>
  );
}


export default function Invoices() {
  const [f, setF] = useState(0);
  const nav = useNavigate();
  const [status, pay] = FILTERS[f];
  const { data, loading } = useFetch<any[]>(`/invoices?status=${status}&payment_status=${pay}`);
  const payments = useFetch<any[]>("/payments");
  const pending = useFetch<any[]>("/invoices-pending/orders");
  const invoice = async (orderId: string) => {
    const r = await act(() => api.post("/invoices/from-order", { order_id: orderId }), "Brouillon créé (facture pour une entreprise, bon de paiement pour un particulier)");
    if (r) nav(`/factures/${r.data.id}`);
  };
  return (
    <div>
      <PageHeader eyebrow="Caisse" title="Facturation & encaissements" actions={<StampReport />} />
      {!!pending.data?.length && (
        <Panel title={`OR clôturés à facturer (${pending.data.length})`} className="mb-6" testId="pending-orders-panel">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {pending.data.map((o) => (
              <div key={o.id} className="flex items-center justify-between gap-3 rounded-sm border border-border p-4" data-testid={`pending-order-${o.id}`}>
                <div><div className="font-mono-data font-semibold">{o.number} · {o.plate}</div><div className="text-xs text-muted-foreground">{o.client_name} · clôturé {fmtDate(o.closed_at)}</div></div>
                <Button onClick={() => invoice(o.id)} data-testid={`pending-invoice-btn-${o.id}`}><ReceiptText className="mr-2 h-4 w-4" />Facturer</Button>
              </div>
            ))}
          </div>
        </Panel>
      )}
      <Tabs defaultValue="invoices">
        <TabsList><TabsTrigger value="invoices" data-testid="tab-invoices">Factures, bons & avoirs</TabsTrigger><TabsTrigger value="payments" data-testid="tab-payments">Encaissements</TabsTrigger></TabsList>
        <TabsContent value="invoices">
          <div className="mb-4 flex flex-wrap gap-2">
            {FILTERS.map((x, i) => (
              <button key={i} onClick={() => setF(i)} className={`h-9 rounded-sm border px-4 text-sm transition-colors ${f === i ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`} data-testid={`invoices-filter-${i}`}>{x[2]}</button>
            ))}
          </div>
          <Panel>
            {loading ? <Loading /> : !data?.length ? <Empty text="Aucune facture. Facturez un OR clôturé." /> : (
              <Table head={["N°", "Type", "Client", "Véhicule", "TTC", "Reste dû", "Date", "Statut"]} testId="invoices-table">
                {data.map((i) => (
                  <tr key={i.id} className={rowCls} onClick={() => nav(`/factures/${i.id}`)} data-testid={`invoice-row-${i.id}`}>
                    <td className={`${cellCls} whitespace-nowrap font-mono-data font-semibold`}>{i.number || "—"}</td>
                    <td className={`${cellCls} text-xs`}>{i.kind === "avoir" ? "Avoir" : i.doc_type === "bon" ? "Bon de paiement" : "Facture"}</td>
                    <td className={cellCls}>{i.client_name}</td>
                    <td className={`${cellCls} font-mono-data`}>{i.plate}</td>
                    <td className={`${cellCls} font-mono-data`}>{da(i.totals.ttc)}</td>
                    <td className={`${cellCls} font-mono-data`}>{i.kind === "facture" && i.status === "validee" ? da(i.balance) : "—"}</td>
                    <td className={`${cellCls} text-muted-foreground`}>{fmtDate(i.validated_at || i.created_at, false)}</td>
                    <td className={cellCls}><StatusBadge status={i.status === "brouillon" ? "brouillon" : i.payment_status} /></td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>
        </TabsContent>
        <TabsContent value="payments">
          <Panel>
            {!payments.data?.length ? <Empty text="Aucun encaissement" /> : (
              <Table head={["Reçu", "Facture", "Client", "Montant", "Mode", "Date", ""]} testId="payments-table">
                {payments.data.map((p) => (
                  <tr key={p.id} className="border-b border-border/60">
                    <td className={`${cellCls} font-mono-data font-semibold`}>{p.number}</td>
                    <td className={`${cellCls} font-mono-data`}>{p.invoice_number}</td>
                    <td className={cellCls}>{p.client_name}</td>
                    <td className={`${cellCls} font-mono-data`}>{da(p.amount)}</td>
                    <td className={cellCls}>{p.amount < 0 ? "Remboursement · " : ""}{PAY_METHODS[p.method]}</td>
                    <td className={`${cellCls} text-muted-foreground`}>{fmtDate(p.at)}</td>
                    <td className={cellCls}><PdfButton kind="receipt" id={p.id} label="Reçu" testId={`receipt-pdf-${p.id}`} /></td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
